import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { optionify } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import {
  isSubstepCompleted,
  resolveContinueAction
} from 'components/shared/OnboardingWizard/lib/resolveContinueAction';

import usePracticeAddressFormik from './lib/usePracticeAddressFormik';

const AddressForm = ({
  addFooterActions,
  setHeaderButtonProps,
  onNext,
  mutateOrchestratorData,
  orchestratorData,
  orchestratorLoading
}) => {
  const { practice } = useOutletContext();
  const { data: statesData } = useStates();
  const statesOptions = optionify(statesData?.states);

  const initialValues = {
    address1: practice?.practiceAddress?.address_ln_1 || '',
    address2: practice?.practiceAddress?.address_ln_2 || '',
    city: practice?.practiceAddress?.city || '',
    state: statesOptions.find((state) => state.value === practice?.practiceAddress?.state) || '',
    zipCode: practice?.practiceAddress?.zip || ''
  };

  const formik = usePracticeAddressFormik(initialValues, async (values) => {
    await mutateOrchestratorData({ ...values, state: values?.state?.value });
  });

  const savedAddress = practice?.practiceAddress;
  const hasSavedAddress = !!(
    savedAddress?.address_ln_1 &&
    savedAddress?.city &&
    savedAddress?.state &&
    savedAddress?.zip
  );

  const handleSubmit = resolveContinueAction({
    isDirty: formik.dirty,
    isCompleted: isSubstepCompleted(orchestratorData),
    hasExistingData: hasSavedAddress,
    onSave: formik.handleSubmit,
    onConfirm: formik.handleSubmit,
    onNext
  });

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: handleSubmit,
        isDisabled: orchestratorLoading || (!formik.isValid && formik.dirty)
      }
    ]);
  }, [
    formik.isValid,
    formik.dirty,
    hasSavedAddress,
    orchestratorData?.handlerStatus,
    orchestratorLoading
  ]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 px-0.5 md:grid-cols-2 sm:grid-cols-1">
        <Input
          data-qa="practice-address-1"
          label="Primary address"
          type="text"
          placeholder="Enter primary address"
          name="address1"
          value={formik.values.address1}
          onChange={formik.handleChange}
          error={formik.touched.address1 && formik.errors.address1}
        />
        <Input
          label="Secondary address"
          data-qa="practice-address-2"
          type="text"
          isOptional
          placeholder="Enter secondary address"
          name="address2"
          value={formik.values.address2}
          onChange={formik.handleChange}
          error={formik.touched.address2 && formik.errors.address2}
        />
        <Input
          label="City"
          data-qa="practice-city"
          type="text"
          placeholder="Enter city"
          name="city"
          value={formik.values.city}
          onChange={formik.handleChange}
          error={formik.touched.city && formik.errors.city}
        />
        <div className="hidden grid-cols-3 gap-3 lg:grid">
          <div className="col-span-2">
            <Select
              label="State"
              inputId="state-select"
              placeholder="Select state"
              name="state"
              options={statesOptions}
              onChange={(state) => formik.setFieldValue('state', state)}
              value={formik.values.state}
              isMulti={false}
              error={formik.touched.state && formik.errors.state}
            />
          </div>
          <div className="col-span-1">
            <Input
              label="Zip code"
              data-qa="practice-zip"
              type="text"
              placeholder="Enter zip code"
              name="zipCode"
              value={formik.values.zipCode}
              onChange={formik.handleChange}
              error={formik.touched.zipCode && formik.errors.zipCode}
            />
          </div>
        </div>
        <div className="lg:hidden">
          <Select
            label="State"
            inputId="state-select-mobile"
            placeholder="Select state"
            name="state"
            options={statesOptions}
            onChange={(state) => formik.setFieldValue('state', state)}
            value={formik.values.state}
            isMulti={false}
            error={formik.touched.state && formik.errors.state}
          />
        </div>
        <div className="lg:hidden">
          <Input
            label="Zip code"
            data-qa="practice-zip-mobile"
            type="text"
            placeholder="Enter zip code"
            name="zipCode"
            value={formik.values.zipCode}
            onChange={formik.handleChange}
            error={formik.touched.zipCode && formik.errors.zipCode}
          />
        </div>
      </div>
    </>
  );
};

export default AddressForm;
